import { useState } from 'react'
import { useAuth } from './useAuth'
import { buildActionErrorFeedback } from '../helpers/buildActionErrorFeedback'
import type { ActionError } from '../services/api/ActionError'
import type { ActionFeedback } from '../types'
import type { UseActionRunnerResult } from './useActionRunner.types'

export type { UseActionRunnerResult }

// Runs a throw-away act request and reduces the result to a single ActionFeedback.
// A 409 becomes a distinct `conflict` feedback; anything else an `error`. Pages can
// supply `mapError` to add endpoint-specific handling (e.g. 404 -> notFound). The
// form is left untouched so the operator can tweak inputs and retry.
export function useActionRunner(): UseActionRunnerResult {
  const { getToken } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [feedback, setFeedback] = useState<ActionFeedback | null>(null)

  async function run(
    actionFn: (token: string) => Promise<string>,
    mapErrorFn?: (error: ActionError) => ActionFeedback | null,
  ) {
    setIsSubmitting(true)
    setFeedback(null)
    try {
      const token = await getToken()
      const message = await actionFn(token)
      setFeedback({ kind: 'success', message })
    } catch (error) {
      setFeedback(buildActionErrorFeedback(error, mapErrorFn))
    } finally {
      setIsSubmitting(false)
    }
  }

  return { isSubmitting, feedback, run }
}
